/* eslint-disable react/forbid-prop-types */
/* eslint-disable import/no-unresolved */
import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  Container,
  Box,
  Typography,
  FormControlLabel,
  Radio,
    Select,
    InputLabel,
  MenuItem,
  FormControl,
  Dialog,
  Alert,
  Chip,
  Divider,
} from "@mui/material";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import TextFieldComponent from "components/TextField";
import ButtonComponent from "components/Button";
import {
  schema,
  getTextfieldData,
  getButtonData,
  getSelectFieldData,
  getRadioButtonData,
  getTextfieldBasicData
} from "./helper";
import RadioButtonFieldComponent from "components/RadioButtonField";
import CustomSelectField from "components/CustomSelectField";
import MapAddressPicker from "components/elements/core/MapAddressPicker";


const ShippingAddressEditor = ({
  setState, setAllStates, shippingAddressEditing, verifyAddressToAccountCall, cancelEdit, countries, getReverseGeocode, reverseGeocodeData, isReverseGeocoding,
                                 addUpdateShippingAddress
}) => {
  const {
    _id, name, cityname, address, pincode, phone, latitude, longitude, countrycode
  } = shippingAddressEditing;

  const [blur, setblur] = React.useState({
    name: false,
    cityname: false,
    address: false,
    pincode: false,
    phone: false,
    countrycode: false,
  });

  const [showMapPicker, setShowMapPicker] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(
    latitude && longitude ? { lat: parseFloat(latitude), lng: parseFloat(longitude) } : null
  );
  const [mapSelectedAddress, setMapSelectedAddress] = useState(null);


  const selectOptions = [
    <MenuItem key="empty" value="">
      <em>Select Country</em>
    </MenuItem>,
    ...countries.map((item) => {
      const { name, countrycode: code } = item;
      return (
          <MenuItem key={code} value={code}>{name}</MenuItem>
      );
    })
  ];

  // Debug current countrycode value
  React.useEffect(() => {

    // Check if current countrycode exists in countries list
    if (countrycode) {
      const matchFound = countries.some(c => c.countrycode === countrycode);
      if (!matchFound) {
        console.warn('Countrycode not found in countries list:', countrycode);
      }
    }
  }, [countrycode, countries]);


  const onFinish = () => {

    if (selectedLocation) {
      // If location is selected from map, proceed with saving the address
      addUpdateShippingAddress({
        _id, name, cityname, address, pincode, phone, latitude, longitude, countrycode
      });

    } else {
      addUpdateShippingAddress({
        _id, name, cityname, address, pincode, phone, latitude: "", longitue: "", countrycode
      });
    }

  };

  const handleMapOpen = () => {
    setShowMapPicker(true);
  };

  const handleMapClose = () => {
    setShowMapPicker(false);
  };

  const handleLocationSelect = (location) => {
    setSelectedLocation(location);
    // Call reverse geocoding API
    if (getReverseGeocode) {
      getReverseGeocode({ lat: location.lat, lng: location.lng });
    }
    setShowMapPicker(false);
  };

  // Update form fields when reverse geocode data arrives
  React.useEffect(() => {
    if (reverseGeocodeData && selectedLocation) {
      setMapSelectedAddress(reverseGeocodeData.formatted_address);
      
      // Collect all updates
      const updates = {
        ...shippingAddressEditing
      };
      
      if (reverseGeocodeData.address) {
        updates.address = reverseGeocodeData.address;
      }
      
      if (reverseGeocodeData.city) {
        updates.cityname = reverseGeocodeData.city;
      }
      
      if (reverseGeocodeData.pincode) {
        updates.pincode = reverseGeocodeData.pincode;
      }
      
      if (reverseGeocodeData.area) {
        updates.area = reverseGeocodeData.area;
      }
      
      if (reverseGeocodeData.state) {
        updates.state = reverseGeocodeData.state;
      }
      
      if (reverseGeocodeData.country) {
        updates.country = reverseGeocodeData.country;
      }

      if (reverseGeocodeData.countrycode) {
        updates.countrycode = reverseGeocodeData.countrycode;
      }

      // Store lat/lng in the address object
      updates.latitude = selectedLocation.lat;
      updates.longitude = selectedLocation.lng;
      updates.place_id = reverseGeocodeData.place_id || "";
      
      // Mark all updated fields as touched
      const touchedFields = {};
      Object.keys(updates).forEach(key => {
        if (key !== '_id' && updates[key] !== shippingAddressEditing[key]) {
          touchedFields[key] = true;
        }
      });
      
      setblur((prev) => ({ ...prev, ...touchedFields }));
      
      // Update all states at once using the parent's setState callback
      // Call setState for each field to trigger parent update
      if (setAllStates) {
        setAllStates(updates);
      } else {
        // Fallback: Update each field individually with small delays
        const fields = Object.keys(updates).filter(key => 
          key !== '_id' && updates[key] !== shippingAddressEditing[key]
        );
        
        fields.forEach((field, index) => {
          setTimeout(() => {
            const event = { target: { value: updates[field] } };
            setState(event, field);
          }, index * 10); // Small delay between each update
        });
      }
    }
  }, [reverseGeocodeData]);

  const handleChange = (event, field) => {
    setState(event, field);
    setblur({ ...blur, [field]: true });

  };

  const hasError = () => {
    try {
      return !schema.validateSync(shippingAddressEditing);
    } catch (err) {
      return true;
    }
  };

  const handleBlur = (field) => {
    setblur({ ...blur, [field]: true });
  };

  const isTouched = () => (blur.name || blur.address || blur.cityname || blur.pincode || blur.phone || blur.countrycode);

  const getError = (field) => {
    if (blur[field] && hasError()) {
      try {
        schema.validateSyncAt(field, shippingAddressEditing);
      } catch (err) {
        return err.message;
      }
    }
    return null;
  };


  return (
    <Container>
      <Box>
        {getTextfieldBasicData(name, phone).map((element) => (
            <Box className="textfield" m={2} key={element.onChangeField}>
              <TextFieldComponent
                  required
                  label={element.label}
                  value={element.value}
                  error={!!getError(element.onChangeField)}
                  helperText={getError(element.onChangeField)}
                  onChange={(event) => handleChange(event, element.onChangeField)}
              />
            </Box>
        ))}
      </Box>

      <Box m={2}>
        <Alert severity="info" icon={<LocationOnIcon />}>
          Select your location on map or enter address manually
        </Alert>
      </Box>

      <Box m={2} display="flex" justifyContent="center">
        <ButtonComponent
          variant="contained"
          type="primary"
          name="📍 Select Location on Map"
          onClick={handleMapOpen}
          startIcon={<LocationOnIcon />}
        />
      </Box>

      {selectedLocation && mapSelectedAddress && (
        <Box m={2}>
          <Alert severity="success">
            <Typography variant="body2">
              <strong>Selected Location:</strong>
            </Typography>
            <Typography variant="caption">
              {mapSelectedAddress}
            </Typography>
            <Box mt={1}>
              <Chip 
                label={`Lat: ${selectedLocation.lat.toFixed(6)}, Lng: ${selectedLocation.lng.toFixed(6)}`} 
                size="small" 
                color="primary" 
                variant="outlined" 
              />
            </Box>
          </Alert>
        </Box>
      )}

      <Divider sx={{ my: 2 }} />

      <Box display="flex" flexWrap="wrap">
        {getTextfieldData(address, pincode, cityname).map((element) => (
          <Box className="textfield" m={2} key={element.onChangeField}>
            <TextFieldComponent
              required
              label={element.label}
              value={element.value}
              error={!!getError(element.onChangeField)}
              helperText={getError(element.onChangeField)}
              onChange={(event) => handleChange(event, element.onChangeField)}
            />
          </Box>
        ))}

         {getSelectFieldData(countrycode || "").map((element) => (
             <Box className="textfield" m={2} key={element.onChangeField}>
               <CustomSelectField
                   required
                   label={element.label}
                   value={element.value || ""}
                   onClick={element.onClick}
                   error={!!getError(element.onChangeField)}
                   helperText={getError(element.onChangeField)}
                   name={element.name}
                   children={selectOptions}
                   onChange={(event) => handleChange(event, element.onChangeField)}
               />
             </Box>
         ))}

      </Box>
      <br />
      <Typography align="center">
        {getButtonData(onFinish, cancelEdit, hasError, isTouched).map((element) => (
          <ButtonComponent
              key={element.name}
              sx={{
                m: 2,
              }}
              variant={element.variant}
              type={element.type}
            onClick={element.onClick}
            disabled={element.disabled}
            name={element.name}
          />
        ))}
      </Typography>

      <Dialog 
        open={showMapPicker} 
        onClose={handleMapClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            height: '90vh',
            maxHeight: '800px',
          }
        }}
      >
        {showMapPicker && (
          <MapAddressPicker
            onLocationSelect={handleLocationSelect}
            onClose={handleMapClose}
            initialLocation={selectedLocation}
            isLoading={isReverseGeocoding}
          />
        )}
      </Dialog>
    </Container>
  );
};

ShippingAddressEditor.propTypes = {
  setState: PropTypes.func.isRequired,
  setAllStates: PropTypes.func,
  shippingAddressEditing: PropTypes.object.isRequired,
  verifyAddressToAccountCall: PropTypes.func.isRequired,
  cancelEdit: PropTypes.func.isRequired,
  cities: PropTypes.array.isRequired,
  getReverseGeocode: PropTypes.func,
  reverseGeocodeData: PropTypes.object,
  isReverseGeocoding: PropTypes.bool,
};

ShippingAddressEditor.defaultProps = {
  setAllStates: null,
  getReverseGeocode: null,
  reverseGeocodeData: null,
  isReverseGeocoding: false,
};

export default ShippingAddressEditor;
